import { css } from '@emotion/css';
import { Award, Users, TrendingUp, Heart } from 'lucide-react';
import { ContactForm } from '../../../shared/components/contact-form';

const page = css({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'var(--spacing-lg)',
  backgroundColor: 'var(--primary-color)',
  minHeight: 'calc(100vh - 100px)',
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-md)',
  },
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-sm)',
  },
});

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xl)',
});

const header = css({
  marginBottom: 'var(--spacing-lg)',
  borderBottom: '2px solid var(--pc-200)',
  paddingBottom: 'var(--spacing-md)',
});

const title = css({
  fontSize: 'var(--fs-800)',
  fontWeight: 700,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-xs)',
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-700)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-600)',
  },
});

const subtitle = css({
  fontSize: 'var(--fs-500)',
  color: 'var(--g-700)',
  fontWeight: 400,
  lineHeight: 1.6,
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-450)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-400)',
  },
});

const section = css({
  marginBottom: 'var(--spacing-lg)',
});

const sectionTitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 600,
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-md)',
  paddingBottom: 'var(--spacing-xs)',
  borderBottom: '1px solid var(--pc-100)',
});

const benefitsGrid = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: 'var(--spacing-lg)',
  marginTop: 'var(--spacing-lg)',
});

const benefitCard = css({
  padding: 'var(--spacing-lg)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.2)',
  borderRadius: 'var(--spacing-md)',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
});

const benefitIcon = css({
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-xs)',
});

const benefitTitle = css({
  fontSize: 'var(--fs-500)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-2xs)',
});

const benefitText = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-700)',
  lineHeight: 1.6,
});

const content = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-800)',
  lineHeight: 1.8,
  marginBottom: 'var(--spacing-md)',
  '& ul': {
    listStyle: 'disc',
    paddingLeft: 'var(--spacing-xl)',
    marginTop: 'var(--spacing-sm)',
    '& li': {
      marginBottom: 'var(--spacing-xs)',
    },
  },
});

export function Sponsor() {
  return (
    <div className={page}>
      <div className={container}>
        <div className={header}>
          <h1 className={title}>Seja um Patrocinador</h1>
          <p className={subtitle}>
            Associe sua marca a experiências únicas e alcance um público engajado e qualificado.
          </p>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Por que patrocinar com a Anchor?</h2>
          <div className={content}>
            <p>
              A Anchor Ingressos conecta marcas a eventos exclusivos e uma audiência apaixonada por
              cultura, entretenimento e experiências memoráveis. Ao se tornar um patrocinador, sua
              empresa ganha visibilidade estratégica e fortalece sua presença no mercado.
            </p>
          </div>

          <div className={benefitsGrid}>
            <div className={benefitCard}>
              <Award className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Visibilidade de Marca</h3>
              <p className={benefitText}>
                Exponha sua marca em eventos de alto impacto e alcance milhares de pessoas que
                compartilham os valores da sua empresa.
              </p>
            </div>

            <div className={benefitCard}>
              <Users className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Público Qualificado</h3>
              <p className={benefitText}>
                Conecte-se com um público engajado, interessado em cultura e entretenimento,
                perfeito para fortalecer sua marca.
              </p>
            </div>

            <div className={benefitCard}>
              <TrendingUp className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Crescimento Mútuo</h3>
              <p className={benefitText}>
                Cresça junto conosco através de parcerias estratégicas que geram valor para ambas
                as partes e para o público.
              </p>
            </div>

            <div className={benefitCard}>
              <Heart className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Experiências Memoráveis</h3>
              <p className={benefitText}>
                Associe sua marca a momentos inesquecíveis e crie conexões emocionais duradouras
                com o público.
              </p>
            </div>
          </div>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Oportunidades de Patrocínio</h2>
          <div className={content}>
            <p>Oferecemos diversos níveis e formatos de patrocínio:</p>
            <ul>
              <li>Patrocínio Master - Máxima visibilidade em todos os canais</li>
              <li>Patrocínio de Evento - Associação direta a eventos específicos</li>
              <li>Patrocínio Digital - Presença em plataformas digitais e redes sociais</li>
              <li>Ações de Experiência - Criação de ativações e experiências de marca</li>
              <li>Cotas de Patrocínio - Pacotes personalizados para diferentes necessidades</li>
            </ul>
          </div>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Entre em Contato</h2>
          <div className={content}>
            <p>
              Interessado em se tornar um patrocinador? Preencha o formulário abaixo e nossa equipe
              entrará em contato para apresentar as melhores opções para sua marca.
            </p>
          </div>
          <ContactForm defaultSubject="Interesse em Patrocínio" />
        </div>
      </div>
    </div>
  );
}

export default Sponsor;
